import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { updateQuery } from 'core/query-client/query-client';
import { QUERY_KEYS } from 'core/query-client/query-keys.constants';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from 'core/utils/notification-service';
import { useMaterialDefinitionsListSelection } from 'modules/settings/contexts/material-definitions/use-material-definitions-list-selection.hook';
import { deleteMaterialDefinition } from 'modules/settings/pods/settings-materials/service/material-definitions/material-definitions.service';
import { BULK_DELETE_MATERIALS_NOTIFICATION_KEY } from './use-material-definitions-bulk-deleter.constants';

export const useMaterialDefinitionsBulkDeleter = () => {
    const { t } = useTranslation();
    const { clearSelection } = useMaterialDefinitionsListSelection();

    const bulkDeleteMutation = usePocMutation({
        mutationFn: async (ids: string[]) => {
            const results = await Promise.allSettled(ids.map((id) => deleteMaterialDefinition(id)));
            const failed = results.filter((result) => result.status === 'rejected').length;
            return { total: ids.length, failed };
        },
        // Promise.allSettled never rejects, so per-id delete failures surface here as `failed`,
        // not through onError. Report all-success, partial, and total-failure outcomes distinctly
        // so the user is not told the whole batch failed when some rows were actually deleted.
        onSuccess: ({ total, failed }) => {
            updateQuery(QUERY_KEYS.MATERIAL_DEFINITIONS);
            clearSelection();
            if (failed === 0) {
                showSuccessNotification(t(BULK_DELETE_MATERIALS_NOTIFICATION_KEY.success, { count: total }));
            } else if (failed === total) {
                showErrorNotification(t(BULK_DELETE_MATERIALS_NOTIFICATION_KEY.error));
            } else {
                showInfoNotification(
                    t(BULK_DELETE_MATERIALS_NOTIFICATION_KEY.partial, { deleted: total - failed, failed })
                );
            }
        },
    });

    return {
        bulkDeleteMaterials: (ids: string[]) => bulkDeleteMutation.mutateAsync(ids),
    };
};
